import React, {
  useMemo,
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Row,
  Col,
  Table,
  Select,
  TreeSelect,
  Dropdown,
  Menu,
  Space,
} from 'antd';
import {
  ReloadOutlined,
  DownOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { TaskHelpers } from 'ontraccr-common';

import MaterialTableAddDrawer from './MaterialTableAddDrawer';
import OnTraccrTextInput from '../../../common/inputs/OnTraccrTextInput';
import TableDeleteButton from './TableDeleteButton';
import OnTraccrButton from '../../../common/buttons/OnTraccrButton';
import OnTraccrNumberInput from '../../../common/inputs/OnTraccrNumberInput';
import DisplayText from '../../../common/text/DisplayText';
import ManualEntryDatePicker from '../../../clock/ManualEntry/ManualEntryDatePicker';
import HoverHelp from '../../../common/HoverHelp';

import {
  computeQuantityRemaining,
  getMaterialRowDivisionIds,
  getPriceFloat,
  hasMarkup,
  MATERIAL_COST_MAX_DECIMALS,
} from '../../../materials/materialsHelpers';
import { getIdMap, isNullOrUndefined, sortByLocalCompare } from '../../../helpers/helpers';
import { updateCostcodeIdMapForTasks } from '../../../costcodes/state/costcodes.actions';
import {
  generateId,
  getHasColumnPermissions,
  getScopedTableCostcodes,
} from '../../formHelpers';
import { floatIsBad } from '../../ResponderHelpers';
import useCheckTableMaxRows from '../../../common/hooks/useCheckTableMaxRows';
import {
  updateStackedDates,
  getCalculationTableColumn,
  getCustomTableColumn,
  LINE_NUMBER_COLUMN,
  createHandleReloadPreload,
  clearVendorOnCustomRows,
  clearCostcodesOnLinkedProjectChange,
  rematchProjectMaterials,
} from './formFieldsHelpers';
import {
  getColumnTitle,
  getCostColumn,
  getPriceColumn,
  getQuantityRemainingColumn,
  getUpdatedDateColumn,
} from './FormFieldColumns';
import TableAttachmentCell from './TableField/TableAttachmentCell';
import { currencyFormatter, materialCostFormatter } from '../../../helpers/inputParsers';
import usePreloadHook from '../../../common/hooks/usePreloadHook';
import Permissions from '../../../auth/Permissions';
import { pendingTableColumn } from '../../forms.constants';
import CostCodeTableSelector from './CostCodeTable/CostCodeTableSelector';
import {
  getEligibleCategoriesForCostcode,
  getFormTableCostcodeSelectValue,
  getFormTableCostcodeUpdateFields,
  formatFormTableCostcodeName,
  getFormTableCostcodeFieldsFromIds,
  getFormTableCostcodeFieldsWithLabourOverride,
  getCostcodeColumnAllowedCategoryIds,
  getNestedTableCostcodeFromLabourSelection,
} from '../../../helpers/costcodeHelpers';
import CustomConfirmModal from '../../../common/modals/CustomConfirmModal';
import { isInDivisions } from '../../../helpers/divisionHelpers';

/*
  name: 'Name',
  description: 'Description',
  partNumber: 'Part Number',
  units: 'Units',
  price: 'Price',
  quantity: 'Quantity',
  total: 'Total',

*/
const defaultRender = ({
  onValueChanged,
  key,
  isDisplay,
  textarea,
}) => function render(val, record) {
  if (isDisplay) return val;
  return (
    <OnTraccrTextInput
      textarea={textarea}
      value={val}
      onChange={(e) => {
        const {
          target: {
            value,
          } = {},
        } = e;
        onValueChanged(record.id, { [key]: value });
      }}
    />
  );
};

// eslint-disable-next-line react/function-component-definition
const totalRender = (isDisplay, formatter = currencyFormatter) => (total) => {
  // eslint-disable-next-line react/destructuring-assignment
  const totalText = total ? formatter(total) : '$0.00';
  if (isDisplay) return totalText;
  return (
    <Row align="middle">
      <DisplayText title={totalText} style={{ marginBottom: 0 }} />
    </Row>
  );
};

const getMaterialColumnMap = ({
  onQuantityChange,
  isDisplay,
  discount,
  onValueChanged,
  globalMaterialLocationsMap,
  globalMaterialLocations,
  requiredColumns,
  preventEdits,
  columnKeyMap,
  isAssemblyMaterial = false,
  costcodeMap,
  costcodes,
  filterCostcode,
  allowedCategoryIds,
  onCostcodeUpdated,
  getMaterialLocations,
  onOpenFile,
  setCurrFileFieldId,
  setCurrAttachmentRowId,
  setOpenFileIndex,
  setCurrFieldId,
  previewProps,
  id: fieldId,
  responding,
  customColumnNames = {},
  vendors = {},
  materials = {},
  divisions = [],
  projectId,
  linkProjectCostCodes = false,
}) => {
  const onUpdatedDateChange = (value, record) => {
    onValueChanged(record.id, { updatedDate: value.join(', ') });
  };

  const isReadOnly = isDisplay || preventEdits || isAssemblyMaterial;

  const updatedDateColumn = getUpdatedDateColumn({
    title: customColumnNames.updatedDate || 'Date Updated',
    requiredColumns,
    onChange: onUpdatedDateChange,
    isDisplay: isReadOnly,
  });

  const materialCostColumn = getCostColumn({
    title: customColumnNames.cost || 'Material Cost',
    dataIndex: 'cost',
    requiredColumns,
    onChange: (value, record) => onValueChanged(record.id, { cost: value }),
    isDisplay: isReadOnly,
    precision: MATERIAL_COST_MAX_DECIMALS,
    step: 0.00001,
    formatter: materialCostFormatter,
  });

  const labourCostColumn = getCostColumn({
    title: customColumnNames.labourCost || 'Labour Cost',
    dataIndex: 'labourCost',
    requiredColumns,
    onChange: (value, record) => onValueChanged(record.id, { labourCost: value }),
    isDisplay: isReadOnly,
  });

  const materialPriceColumn = getPriceColumn({
    title: customColumnNames.price || 'Material Price',
    dataIndex: 'price',
    requiredColumns,
    onChange: (value, record) => onValueChanged(record.id, { price: value }),
    isDisplay: isReadOnly,
    discount,
  });

  const labourPriceColumn = getPriceColumn({
    title: customColumnNames.labourPrice || 'Labour Price',
    dataIndex: 'labourPrice',
    requiredColumns,
    onChange: (value, record) => onValueChanged(record.id, { labourPrice: value }),
    isDisplay: isReadOnly,
    discount,
  });

  const columns = {
    name: {
      title: getColumnTitle({ requiredColumns, title: customColumnNames.name || 'Name' }),
      dataIndex: 'name',
      width: 125,
      render: defaultRender({
        onValueChanged, key: 'name', isDisplay: isReadOnly, textarea: true,
      }),
    },
    description: {
      title: getColumnTitle({ requiredColumns, title: customColumnNames.description || 'Description' }),
      dataIndex: 'description',
      width: 200,
      render: defaultRender({
        onValueChanged, key: 'description', isDisplay: isReadOnly, textarea: true,
      }),
    },
    partNumber: {
      title: getColumnTitle({ requiredColumns, title: customColumnNames.partNumber || 'Part Number' }),
      dataIndex: 'partNumber',
      width: 100,
      render: defaultRender({ onValueChanged, key: 'partNumber', isDisplay: isReadOnly }),
    },
    units: {
      title: getColumnTitle({ requiredColumns, title: customColumnNames.units || 'Units' }),
      dataIndex: 'units',
      width: 100,
      render: defaultRender({ onValueChanged, key: 'units', isDisplay: isReadOnly }),
    },
    cost: materialCostColumn,
    labourCost: labourCostColumn,
    price: materialPriceColumn,
    labourPrice: labourPriceColumn,
    quantity: {
      title: getColumnTitle({ requiredColumns, title: customColumnNames.quantity || 'Quantity' }),
      dataIndex: 'quantity',
      width: 100,
      render: (quantity, record) => {
        if (isDisplay || isAssemblyMaterial) return quantity;
        return (
          <Row align="middle">
            <OnTraccrNumberInput
              min={0}
              precision={2}
              value={quantity}
              onChange={(value) => onQuantityChange({ id: record.id, value })}
            />
          </Row>
        );
      },
    },
    currentQuantity: {
      title: customColumnNames.currentQuantity || 'Current Quantity',
      dataIndex: 'currentQuantity',
      width: 100,
      render: (currentQuantity) => currentQuantity,
    },
    quantityAllocated: {
      title: customColumnNames.quantityAllocated || 'Quantity Allocated',
      dataIndex: 'quantityAllocated',
      width: 100,
      render: (quantityAllocated, record) => {
        const { quantityAllocated: { isReadOnly: qtyIsReadOnly = false } } = columnKeyMap;
        if (isReadOnly || qtyIsReadOnly || record.isAssembly) {
          return quantityAllocated;
        }
        return (
          <OnTraccrNumberInput
            key={record.id}
            min={0}
            precision={2}
            value={quantityAllocated}
            defaultValue={quantityAllocated}
            onChange={(newQuantityAllocated) => (
              onValueChanged(record.id, { quantityAllocated: newQuantityAllocated })
            )}
          />
        );
      },
    },
    quantityRemaining: getQuantityRemainingColumn({
      title: customColumnNames.quantityRemaining || 'Quantity Remaining',
      quantityRemainingMode: columnKeyMap.quantityRemaining?.quantityRemainingMode,
    }),
    total: {
      title: customColumnNames.total || 'Total Material Cost',
      dataIndex: 'total',
      width: 100,
      render: totalRender(isDisplay || isAssemblyMaterial),
    },
    labourCostTotal: {
      title: customColumnNames.labourCostTotal || 'Total Labour Cost',
      dataIndex: 'labourCostTotal',
      width: 100,
      render: totalRender(isDisplay || isAssemblyMaterial),
    },
    labourPriceTotal: {
      title: customColumnNames.labourPriceTotal || 'Total Labour Price',
      dataIndex: 'labourPriceTotal',
      width: 100,
      render: totalRender(isDisplay || isAssemblyMaterial),
    },
    labourAndMaterialTotal: {
      title: customColumnNames.labourAndMaterialTotal || 'Material Total',
      dataIndex: 'labourAndMaterialTotal',
      width: 100,
      render: totalRender(isDisplay || isAssemblyMaterial),
    },
    location: {
      title: getColumnTitle({ requiredColumns, title: customColumnNames.location || 'Location' }),
      dataIndex: 'locationId',
      width: 250,
      render: (locationId, record) => {
        if (isDisplay) return record.locationText;
        const opts = getMaterialLocations(record);
        if (opts.length === 1) {
          const [opt] = opts;
          if (!locationId) {
            onValueChanged(record.id, {
              locationId: opt.value,
              locationText: opt.label,
              globalMaterialLocationsId: opt.globalMaterialLocationsId,
              equipmentId: opt.equipmentId,
              isDefaultUpdate: true,
            });
          }
          return opt.label;
        }

        return (
          <Select
            options={opts}
            optionFilterProp="label"
            style={{ width: 250 }}
            value={record.locationId}
            onChange={(_, opt) => {
              onValueChanged(record.id, {
                locationId: opt.value,
                locationText: opt.label,
                globalMaterialLocationsId: opt.globalMaterialLocationsId,
                equipmentId: opt.equipmentId,
              });
            }}
          />
        );
      },
    },
    debitOrCredit: {
      title: getColumnTitle({ requiredColumns, title: customColumnNames.debitOrCredit || 'Debit/Credit' }),
      dataIndex: 'debitOrCredit',
      width: 150,
      render: (debitOrCredit, record) => {
        if (isDisplay) return debitOrCredit;
        const opts = [
          { label: 'Debit', value: 'Debit' },
          { label: 'Credit', value: 'Credit' },
        ];
        return (
          <Select
            options={opts}
            optionFilterProp="label"
            style={{ width: 150 }}
            value={debitOrCredit}
            onChange={(_, opt) => {
              onValueChanged(record.id, { debitOrCredit: opt.value });
            }}
          />
        );
      },
    },
    toLocation: {
      title: getColumnTitle({ requiredColumns, title: customColumnNames.toLocation || 'To Location' }),
      dataIndex: 'toLocation',
      width: 250,
      render: (toLocation, record) => {
        if (isDisplay || isAssemblyMaterial) return record.toLocationText;
        const existingOpts = getMaterialLocations(record);
        const existingOptsMap = getIdMap(existingOpts, 'label');
        const globalOpts = globalMaterialLocations.map((loc) => {
          const { id: locId, locationText } = loc;
          return {
            label: locationText,
            value: locId,
            isLeaf: true,
            globalMaterialLocationsId: locId,
          };
        }).filter((loc) => !existingOptsMap[loc.label]);

        const treeData = [];

        if (existingOpts.length) {
          treeData.push({
            value: 'assignedLocations',
            title: 'Assigned Locations',
            children: existingOpts,
            selectable: false,
          });
        }

        if (globalOpts.length) {
          treeData.push({
            value: 'globalLocations',
            title: 'Global Locations',
            children: globalOpts,
            selectable: false,
          });
        }

        return (
          <TreeSelect
            showSearch
            treeDefaultExpandAll
            treeNodeFilterProp="label"
            treeData={treeData}
            style={{ width: 250 }}
            value={record.toLocation}
            onSelect={(_, opt) => {
              onValueChanged(record.id, {
                toLocation: opt.value,
                toLocationText: opt.label,
                toGlobalMaterialLocationsId: opt.globalMaterialLocationsId,
                toEquipmentId: opt.equipmentId,
                isGlobalToLocation: !!globalMaterialLocationsMap[opt.value],
              });
            }}
          />
        );
      },
    },
    addedDate: {
      title: getColumnTitle({ requiredColumns, title: customColumnNames.addedDate || 'Date Added' }),
      dataIndex: 'addedDate',
      width: 250,
      render: (date, record) => {
        if (isReadOnly) return date;

        return (
          <ManualEntryDatePicker
            value={date}
            onChange={(newDate) => onValueChanged(record.id, { addedDate: newDate })}
          />
        );
      },
    },
    updatedDate: updatedDateColumn,
    notes: {
      title: getColumnTitle({ requiredColumns, title: customColumnNames.notes || 'Notes' }),
      dataIndex: 'notes',
      width: 250,
      render: (notes, record) => {
        if (isReadOnly) return notes;
        return (
          <OnTraccrTextInput
            key={record.id}
            value={notes}
            onChange={(e) => {
              const {
                target: {
                  value,
                } = {},
              } = e;
              onValueChanged(record.id, { notes: value });
            }}
            textarea
          />
        );
      },
    },
    supplier: {
      title: getColumnTitle({ requiredColumns, title: customColumnNames.supplier || 'Supplier' }),
      dataIndex: 'supplier',
      width: 100,
      render: defaultRender({ onValueChanged, key: 'supplier', isDisplay: isReadOnly }),
    },
    vendor: {
      title: getColumnTitle({ requiredColumns, title: customColumnNames.vendor || 'Vendor' }),
      dataIndex: 'vendorId',
      width: 150,
      render: (vendorId, record) => {
        if (isReadOnly) {
          const vendor = vendors[vendorId];
          return vendor ? vendor.name : '';
        }

        const material = materials[record.globalMaterialId || record.materialId];

        /*
          A material can live in several divisions, and a vendor is valid as long as it
          shares any one of them.
        */
        const rowDivisionIds = getMaterialRowDivisionIds({
          record,
          materials,
          formDivisionId: divisions[0],
        });
        const rowDivisionSet = new Set(rowDivisionIds.filter(Boolean));

        const recordVendorId = vendorId ?? (material?.vendorId || null);

        const filteredVendors = Object.values(vendors).filter((vendor) => vendor.active
          && (!rowDivisionSet.size || isInDivisions(vendor, rowDivisionSet)));

        const sortedVendors = filteredVendors.toSorted(sortByLocalCompare('name'));

        const vendorOptions = sortedVendors.map((vendor) => (
          {
            label: vendor.name,
            value: vendor.id,
          }
        ));

        return (
          <Select
            value={recordVendorId}
            onChange={(value) => onValueChanged(record.id, { vendorId: value, vendor: value ? (vendors[value]?.name ?? '') : '' })}
            optionFilterProp="label"
            allowClear
            showSearch
            placeholder="Select a Vendor"
            style={{ width: 150 }}
            options={vendorOptions}
          />
        );
      },
    },
    costcode: {
      title: getColumnTitle({ requiredColumns, title: customColumnNames.costcode || 'Cost Code' }),
      dataIndex: 'costcodeId',
      width: 250,
      render: (_, record) => {
        const costcodeProjectId = linkProjectCostCodes ? projectId : undefined;
        if (isReadOnly) {
          return record.costcode || costcodeMap[record.costcodeId]?.name || '';
        }

        return (
          <CostCodeTableSelector
            projectId={costcodeProjectId}
            onCostcodeUpdated={onCostcodeUpdated}
            value={getFormTableCostcodeSelectValue(record, { costcodeMap })}
            entryId={record.id}
            costcodes={costcodes}
            costcodeMap={costcodeMap}
            categoryFilter="quantityTracking"
            allowedCategoryIds={allowedCategoryIds}
            filterCostcode={filterCostcode}
            globalOnly={!costcodeProjectId}
            style={{ width: '100%' }}
          />
        );
      },
    },
    sourceForm: {
      title: customColumnNames.sourceForm || 'Source Form',
      dataIndex: 'sourceForm',
      width: 200,
      render: (val) => val,
    },
    attachments: {
      title: getColumnTitle({ requiredColumns, title: customColumnNames.attachments || 'Attachments' }),
      dataIndex: 'attachments',
      width: 220,
      render: (attachments, record) => (
        <TableAttachmentCell
          value={attachments}
          isDisplay={isReadOnly}
          onChange={(next) => onValueChanged(record.id, { attachments: next })}
          onOpenFile={onOpenFile}
          setCurrFileFieldId={setCurrFileFieldId}
          setCurrAttachmentRowId={setCurrAttachmentRowId}
          setOpenFileIndex={setOpenFileIndex}
          setCurrFieldId={setCurrFieldId}
          attachmentCellId={fieldId && record?.id ? `${fieldId}-${record.id}` : undefined}
          id={fieldId}
          rowId={record?.id}
          previewProps={previewProps}
          responding={responding}
        />
      ),
    },
  };

  return columns;
};

const materialColumns = ({
  onDelete,
  onQuantityChange,
  onValueChanged,
  columns,
  columnKeyMap,
  isDisplay,
  discount,
  equipmentIdMap,
  globalMaterialLocationsMap,
  globalMaterialLocations,
  requiredColumns,
  materials,
  preventEdits,
  isAssemblyMaterial = false,
  columnPermissions = {},
  userPositionId,
  hasPendingTableData,
  costcodeMap,
  costcodes,
  filterCostcode,
  allowedCategoryIds,
  onCostcodeUpdated,
  getMaterialLocations,
  onOpenFile,
  setCurrFileFieldId,
  setCurrAttachmentRowId,
  setOpenFileIndex,
  setCurrFieldId,
  previewProps,
  id,
  responding,
  vendors = {},
  divisions = [],
  projectId,
  linkProjectCostCodes = false,
  responses,
}) => {
  const cols = [];
  if (!isAssemblyMaterial) {
    cols.push(LINE_NUMBER_COLUMN);
  }

  // Create custom column names map from columns prop
  const customColumnNames = columns.reduce((acc, col) => {
    acc[col.key] = col.name;
    return acc;
  }, {});

  const materialColumnMap = getMaterialColumnMap({
    onQuantityChange,
    isDisplay,
    discount,
    onValueChanged,
    equipmentIdMap,
    globalMaterialLocationsMap,
    globalMaterialLocations,
    requiredColumns,
    materials,
    preventEdits,
    columnKeyMap,
    isAssemblyMaterial,
    costcodeMap,
    costcodes,
    filterCostcode,
    allowedCategoryIds,
    onCostcodeUpdated,
    getMaterialLocations,
    onOpenFile,
    setCurrFileFieldId,
    setCurrAttachmentRowId,
    setOpenFileIndex,
    setCurrFieldId,
    previewProps,
    id,
    responding,
    customColumnNames,
    vendors,
    divisions,
    projectId,
    linkProjectCostCodes,
  });

  if (hasPendingTableData) {
    cols.push(pendingTableColumn);
  }

  columns.forEach((col) => {
    if (!getHasColumnPermissions({
      columnPermissions,
      column: col,
      userPositionId,
      userId: Permissions.id,
    })) {
      return;
    }
    if (col.key in materialColumnMap) {
      cols.push(materialColumnMap[col.key]);
    } else if (col.isCustomText) {
      cols.push(getCustomTableColumn(col, {
        width: 100,
        isDisplay,
        preventEdits,
        requiredColumns,
        responses,
        onChange: (record, value) => onValueChanged(record.id, { [col.key]: value }),
      }));
    } else if (col.isCalculation) {
      cols.push(getCalculationTableColumn(col, {
        width: 100,
        isDisplay,
      }));
    }
  });
  if (!isDisplay && !isAssemblyMaterial) {
    cols.push({
      title: '',
      dataIndex: '',
      width: 100,
      render: (_, record) => (
        <TableDeleteButton onConfirm={() => onDelete(record.id)} />
      ),
    });
  }
  return cols;
};

const decorateMaterial = (
  material = {},
  discount = 0,
  quantityRemainingMode = 'live',
) => {
  const newMaterial = { ...material };
  const {
    userPrice,
    userLabourPrice,
    debitOrCredit,
    labourCost,
    locationId,
    locations = [],
    quantity,
    quantityAllocated,
    isPreloaded,
  } = material;

  if (!debitOrCredit) newMaterial.debitOrCredit = 'Debit';

  const badPrice = floatIsBad(userPrice);
  const badLabourPrice = floatIsBad(userLabourPrice);
  const hasUserPrice = !isNullOrUndefined(userPrice);
  const hasUserLabourPrice = !isNullOrUndefined(userLabourPrice);
  const costNumber = Number(material.cost);
  const labourCostNumber = Number(labourCost);

  const floatQuantity = parseFloat(quantity ?? 0);
  const safeQuantity = Number.isNaN(floatQuantity) ? 0 : floatQuantity;

  if (
    (hasUserPrice && !badPrice)
    || (!hasUserPrice && !Number.isNaN(costNumber) && material.cost && hasMarkup(material.markup))
  ) {
    const price = hasUserPrice
      ? userPrice
      : getPriceFloat(
        material.cost,
        material.markup * 100,
        discount,
      ); // Takes markup as percentage

    const floatPrice = parseFloat(price);

    newMaterial.price = floatPrice;
    newMaterial.labourAndMaterialTotal = floatPrice * safeQuantity;
  } else {
    newMaterial.labourAndMaterialTotal = 0;
  }

  const floatCost = parseFloat(material.cost);
  if (!Number.isNaN(costNumber) && !Number.isNaN(floatCost)) {
    newMaterial.total = Number.parseFloat((floatCost * safeQuantity).toFixed(2));
  } else {
    newMaterial.total = 0;
  }

  if (
    (hasUserLabourPrice && !badLabourPrice)
    || (!hasUserLabourPrice
        && !Number.isNaN(labourCostNumber)
        && labourCost && hasMarkup(material.labourMarkup))
  ) {
    const labourPrice = hasUserLabourPrice
      ? userLabourPrice
      : getPriceFloat(
        labourCostNumber,
        material.labourMarkup * 100,
        discount,
      ); // Takes markup as percentage

    const floatLabourPrice = parseFloat(labourPrice);

    newMaterial.labourPrice = floatLabourPrice;
    newMaterial.labourPriceTotal = floatLabourPrice * safeQuantity;
  } else {
    newMaterial.labourPriceTotal = 0;
  }

  if (!Number.isNaN(labourCostNumber)) {
    const floatLabourCost = parseFloat(labourCostNumber);
    newMaterial.labourCostTotal = floatLabourCost * safeQuantity;
  } else {
    newMaterial.labourCostTotal = 0;
  }

  if (!isPreloaded) {
    // populate current quantity
    if (!locationId) {
      const totalQuantity = locations?.reduce((acc, loc) => (
        acc + ((loc?.quantity ?? 0) - (loc?.quantityAllocated ?? 0))
      ), 0);
      newMaterial.currentQuantity = totalQuantity;
    } else {
      const ourLocation = locations?.find((loc) => loc.id === locationId);
      newMaterial.currentQuantity = (ourLocation?.quantity ?? 0)
        - (ourLocation?.quantityAllocated ?? 0);
    }
  }

  if (quantityRemainingMode) {
    newMaterial.quantityRemaining = computeQuantityRemaining({
      quantity,
      quantityAllocated,
      quantityRemaining: newMaterial.quantityRemaining,
      quantityRemainingMode,
    });
  }

  return newMaterial;
};

const getRelevantKey = (newData) => {
  if (newData.globalMaterialLocationsId) {
    return {
      newDataKey: 'globalMaterialLocationsId',
      materialKey: 'globalMaterialLocationsId',
    };
  }
  if (newData.equipmentId) {
    return {
      newDataKey: 'equipmentId',
      materialKey: 'equipmentId',
    };
  }
  if (newData.toGlobalMaterialLocationsId) {
    return {
      newDataKey: 'toGlobalMaterialLocationsId',
      materialKey: 'globalMaterialLocationsId',
      isToLocation: true,
    };
  }
  if (newData.toEquipmentId) {
    return {
      newDataKey: 'toEquipmentId',
      materialKey: 'equipmentId',
      isToLocation: true,
    };
  }
  return null;
};

const clearInventoryFields = (material = {}) => ({
  ...material,
  locations: [],
  assemblyMaterials: [],
  locationId: undefined,
  locationText: undefined,
  toLocation: undefined,
  toLocationText: undefined,
  toEquipmentId: undefined,
  currentQuantity: undefined,
  quantityAllocated: undefined,
});

const applyProjectMaterialSnapshot = ({
  row = {},
  projectMaterial,
  costcodeMap = {},
  categoriesIdMap = {},
  allowedCategoryIds,
  vendors = {},
  shouldClearCostcodes = false,
}) => clearInventoryFields({
  ...row,
  materialId: projectMaterial.id,
  globalMaterialId: projectMaterial.globalMaterialId ?? null,
  name: projectMaterial.name,
  description: projectMaterial.description,
  code: projectMaterial.code,
  partNumber: projectMaterial.partNumber,
  supplier: projectMaterial.supplier,
  vendorId: projectMaterial.vendorId,
  vendor: vendors[projectMaterial.vendorId]?.name ?? '',
  units: projectMaterial.units,
  cost: projectMaterial.cost,
  markup: projectMaterial.markup,
  labourCost: projectMaterial.labourCost,
  labourMarkup: projectMaterial.labourMarkup,
  ...(shouldClearCostcodes ? {} : getFormTableCostcodeFieldsFromIds({
    costcodeId: projectMaterial.costcodeId,
    phaseId: projectMaterial.phaseId,
    costcodeMap,
    categoriesIdMap,
    categoryFilter: 'quantityTracking',
    allowedCategoryIds,
  })),
  divisionId: projectMaterial.divisionId,
  userPrice: undefined,
  userLabourPrice: undefined,
  addedDate: projectMaterial.createdAt,
  updatedDate: projectMaterial.lastUpdated,
});

export default function MaterialTablePreview({
  title = null,
  materials = {},
  projectMaterials,
  columns = [],
  previewProps = {},
  setPreviewProps,
  isDisplay,
  id,
  setResponses,
  responses = {},
  responding = false,
  projectId,
  customerId,
  configProps,
  showCondensedView,
  costcodes = [],
  divisions = [],
  onOpenFile,
  setCurrFileFieldId,
  setCurrAttachmentRowId,
  setOpenFileIndex,
  setCurrFieldId,
  timecardLabourCostcodeKey = null,
  timecardLabourPhaseId = null,
}) {
  const MATERIAL_CATEGORY_FILTER = 'quantityTracking';
  const {
    hideAddNewButton,
    requiredColumns,
    preventEdits,
    columnPermissions = {},
    linkProjectCostCodes = false,
    linkProjectCostCodesDropdown,
  } = configProps ?? {};
  const values = previewProps.values || []; // For Responses
  const {
    selected: previewSelected,
  } = previewProps;
  const {
    [id]: {
      values: responseSelected,
    } = {},
  } = responses;

  const rawSelected = responding ? responseSelected : previewSelected;

  const selected = useMemo(() => {
    const newId = generateId();
    return rawSelected?.map((s, idx) => (
      // Backwards Compatability - HARBOUR-4193
      s.materialId ? s : { ...s, materialId: s.id, id: newId + idx }
    )) ?? [];
  }, [rawSelected]);

  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const dispatch = useDispatch();

  const positionNames = useSelector((state) => state.settings.positionNames);
  const vendors = useSelector((state) => state.vendors.vendors);

  const users = useSelector((state) => state.users.users);
  const customers = useSelector((state) => state.customers.customers);
  const projects = useSelector((state) => state.projects.projects);
  const equipment = useSelector((state) => state.equipment.equipment);
  const globalMaterialLocations = useSelector(
    (state) => state.globalMaterialLocations.globalMaterialLocations,
  );
  const categoriesIdMap = useSelector((state) => state.costcodes.categoriesIdMap);
  const allowedCostCodeCategoryIds = useMemo(
    () => getCostcodeColumnAllowedCategoryIds(columns),
    [columns],
  );

  const {
    costcodes: tableCostcodes,
    linkedProjectId: linkedProjectIdForCostCodes,
  } = useMemo(() => getScopedTableCostcodes({
    costcodes,
    linkProjectCostCodes,
    linkProjectCostCodesDropdown,
    responses,
  }), [costcodes, linkProjectCostCodes, linkProjectCostCodesDropdown, responses]);

  const costcodeMap = useMemo(() => getIdMap(tableCostcodes), [tableCostcodes]);

  const filterCostcode = useCallback((cc) => {
    if (!cc) return false;
    const divSet = new Set(divisions);
    return cc.active
      && divSet.has(cc.divisionId)
      && getEligibleCategoriesForCostcode(cc, categoriesIdMap, {
        filter: MATERIAL_CATEGORY_FILTER,
        allowedCategoryIds: allowedCostCodeCategoryIds,
      }).length > 0;
  }, [divisions, categoriesIdMap, allowedCostCodeCategoryIds]);

  const labourMappedCostcodeFields = useMemo(() => (
    getNestedTableCostcodeFromLabourSelection({
      labourCostcodeKey: timecardLabourCostcodeKey,
      labourPhaseId: timecardLabourPhaseId,
      costcodeMap,
      categoriesIdMap,
      categoryFilter: MATERIAL_CATEGORY_FILTER,
      allowedCategoryIds: allowedCostCodeCategoryIds,
    })
  ), [
    timecardLabourCostcodeKey,
    timecardLabourPhaseId,
    costcodeMap,
    categoriesIdMap,
    allowedCostCodeCategoryIds,
  ]);
  const phasedCostcodes = useSelector((state) => state.costcodes.phases);
  const phaseMap = useMemo(() => getIdMap(phasedCostcodes), [phasedCostcodes]);

  const columnKeyMap = useMemo(() => getIdMap(columns, 'key'), [columns]);

  const quantityRemainingMode = columnKeyMap.quantityRemaining?.quantityRemainingMode;
  const equipmentIdMap = useMemo(() => getIdMap(equipment), [equipment]);
  const globalMaterialLocationsMap = useMemo(() => (
    getIdMap(globalMaterialLocations)
  ), [globalMaterialLocations]);
  const projectMap = useMemo(() => getIdMap(projects), [projects]);
  const positionNameMap = useMemo(() => getIdMap(positionNames, 'name'), [positionNames]);
  const userMap = useMemo(() => getIdMap(users), [users]);
  const userPositionId = useMemo(() => {
    const userPosition = userMap[Permissions.id]?.position;
    return positionNameMap[userPosition]?.id;
  }, [positionNameMap, userMap]);

  const projectMaterialsMap = useMemo(
    () => getIdMap(projectMaterials),
    [projectMaterials],
  );
  const projectMaterialsByGlobalId = useMemo(() => {
    const map = {};
    projectMaterials?.forEach((material) => {
      if (material.globalMaterialId) {
        map[material.globalMaterialId] = material;
      }
    });
    return map;
  }, [projectMaterials]);

  const discount = useMemo(() => {
    const {
      [projectId]: {
        materialDiscount: projectMaterialDiscount,
        customerId: projectCustomerId = customerId,
      } = {},
    } = projectMap;
    const {
      [projectCustomerId]: {
        materialDiscount: customerMaterialDiscount = 0,
      } = {},
    } = customers;
    return projectMaterialDiscount || customerMaterialDiscount;
  }, [projectMap, projectId, customerId, customers]);

  const decoratedSelected = useMemo(() => (
    selected.map((mat) => decorateMaterial(mat, discount, quantityRemainingMode))
  ), [selected, discount, quantityRemainingMode]);

  const { triggerPreload } = usePreloadHook({
    responses,
    id,
    setResponses,
    columns,
    responding,
    configProps,
    type: 'Materials',
    setLoading,
    divisions,
    costcodeMap,
  });

  useEffect(() => {
    // If the user change the customer or project it will change
    // discount and therefore the price/total
    // We need to push that back to the responses.
    if (!responding) return;
    const shouldUpdate = decoratedSelected.some((decorated, i) => {
      const selectedMat = selected[i];
      return decorated.price !== selectedMat.price || decorated.total !== selectedMat.total;
    });
    if (!shouldUpdate) return;
    setResponses({
      ...responses,
      [id]: {
        ...(responses[id]),
        values: decoratedSelected,
        columns,
      },
    });
  }, [decoratedSelected, selected, responding]);

  const showAddDrawer = useCallback(() => setShowAdd(true), []);
  const closeAddDrawer = useCallback(() => setShowAdd(false), []);

  const updateSelected = useCallback((newSelected) => {
    if (responding) {
      setResponses((prev = {}) => ({
        ...prev,
        [id]: {
          ...(prev[id]),
          values: newSelected,
          columns,
        },
      }));
    } else {
      setPreviewProps({
        ...previewProps,
        selected: newSelected,
      });
    }
  }, [
    responding,
    id,
    columns,
    previewProps,
    setResponses,
    setPreviewProps,
  ]);

  useEffect(() => {
    const { changed, nextSelected } = clearVendorOnCustomRows({ selected, vendors, divisions });
    if (changed) updateSelected(nextSelected);
  }, [divisions, selected, vendors, updateSelected]);

  const skipInitialProjectMaterialsRematch = useRef(true);
  const lastRematchedProjectIdRef = useRef(projectId); // from dropdown
  const prevLinkedProjectIdForCostCodes = useRef(linkedProjectIdForCostCodes); // from config

  // Rematch project materials then clear cost codes when project changes
  useEffect(() => {
    if (isDisplay || !responding) return;

    const {
      changed: shouldRematch,
      nextSkipInitial,
      nextLastRematchedProjectId,
    } = rematchProjectMaterials({
      skipInitial: skipInitialProjectMaterialsRematch.current,
      lastRematchedProjectId: lastRematchedProjectIdRef.current,
      projectId,
      projectMaterials,
    });
    skipInitialProjectMaterialsRematch.current = nextSkipInitial;
    lastRematchedProjectIdRef.current = nextLastRematchedProjectId;

    let nextSelected = selected;
    if (shouldRematch) {
      nextSelected = [];
      selected.forEach((row) => {
        if (row.isCustom) {
          nextSelected.push(row);
        } else if (row.globalMaterialId) { // Project materials from global
          const matched = projectMaterialsByGlobalId[row.globalMaterialId];
          if (matched) {
            nextSelected.push(decorateMaterial(
              applyProjectMaterialSnapshot({
                row,
                projectMaterial: matched,
                costcodeMap,
                categoriesIdMap,
                allowedCategoryIds: allowedCostCodeCategoryIds,
                vendors,
                shouldClearCostcodes: true,
              }),
              discount,
              quantityRemainingMode,
            ));
          }
        } else if (materials[row.materialId]) { // global materials
          nextSelected.push(row);
        }
      });
    }

    const {
      changed: shouldClearCostcodes,
      nextSelected: clearedSelected,
      nextPrevLinkedProjectId,
    } = clearCostcodesOnLinkedProjectChange({
      selected: nextSelected,
      linkedProjectId: linkedProjectIdForCostCodes,
      prevLinkedProjectId: prevLinkedProjectIdForCostCodes.current,
      linkProjectCostCodes,
      responding,
      isDisplay,
    });
    prevLinkedProjectIdForCostCodes.current = nextPrevLinkedProjectId;

    if (!shouldRematch && !shouldClearCostcodes) return;
    updateSelected(shouldClearCostcodes ? clearedSelected : nextSelected);
  }, [
    isDisplay,
    responding,
    projectId,
    projectMaterials,
    projectMaterialsByGlobalId,
    materials,
    costcodeMap,
    categoriesIdMap,
    allowedCostCodeCategoryIds,
    vendors,
    discount,
    quantityRemainingMode,
    linkedProjectIdForCostCodes,
    linkProjectCostCodes,
    selected,
    updateSelected,
  ]);

  const onAddNew = useCallback(() => {
    const newRow = {
      id: generateId(),
      materialId: generateId(),
      isCustom: true,
      addedDate: TaskHelpers.formatDate(DateTime.local().toMillis()),
      updatedDate: DateTime.local().toISO(),
    };
    if (allowedCostCodeCategoryIds?.length && labourMappedCostcodeFields?.costcodeId) {
      Object.assign(newRow, labourMappedCostcodeFields);
    }
    updateSelected(selected.concat([newRow]));
  }, [selected, updateSelected, allowedCostCodeCategoryIds, labourMappedCostcodeFields]);

  const onSelect = useCallback(async (materialIds) => {
    const newId = generateId();
    const globalMaterials = materialIds
      .filter((materialId) => !projectMaterialsMap[materialId])
      .map((materialId) => materials[materialId])
      .filter(Boolean);
    const allCostcodeMap = await dispatch(updateCostcodeIdMapForTasks(globalMaterials));

    const newMaterials = materialIds
      .map((materialId, idx) => {
        const projectMaterial = projectMaterialsMap[materialId];
        if (projectMaterial) {
          const newMaterial = applyProjectMaterialSnapshot({
            row: {
              id: newId + idx,
              addedDate: TaskHelpers.formatDate(DateTime.local().toMillis()),
              updatedDate: DateTime.local().toISO(),
            },
            projectMaterial,
            costcodeMap,
            categoriesIdMap,
            allowedCategoryIds: allowedCostCodeCategoryIds,
            vendors,
          });
          if (allowedCostCodeCategoryIds?.length && labourMappedCostcodeFields?.costcodeId) {
            Object.assign(newMaterial, labourMappedCostcodeFields);
          }
          delete newMaterial.quantity;
          return decorateMaterial(newMaterial, discount, quantityRemainingMode);
        }

        const relevantMaterial = materials[materialId];
        if (!relevantMaterial) return null;

        const relevantAssemblyMaterials = [];

        relevantMaterial?.assemblyMaterials?.forEach((assemblyMaterial) => {
          const { assemblyId, materialId: assemblyMaterialId, quantity = 0 } = assemblyMaterial;
          if (materials[assemblyMaterialId]) {
            relevantAssemblyMaterials.push(decorateMaterial({
              ...materials[assemblyMaterialId],
              id: `${assemblyId}.${assemblyMaterialId}`,
              quantity,
              originalQuantity: quantity,
            }, discount, quantityRemainingMode));
          }
        });

        const newMaterial = {
          ...relevantMaterial,
          quantityAllocated: null, // [2024-10-04] Should not map from material as of right now
          // Do not invent from inventory; set via live calc or mapping
          ...(quantityRemainingMode ? { quantityRemaining: null } : {}),
          materialId,
          globalMaterialId: null,
          id: newId + idx,
          addedDate: TaskHelpers.formatDate(DateTime.local().toMillis()),
          updatedDate: DateTime.local().toISO(),
          assemblyMaterials: relevantAssemblyMaterials,
          ...getFormTableCostcodeFieldsWithLabourOverride({
            costcodeId: relevantMaterial.costcodeId,
            costcodeMap,
            categoriesIdMap,
            categoryFilter: MATERIAL_CATEGORY_FILTER,
            allowedCategoryIds: allowedCostCodeCategoryIds,
            labourMappedCostcodeFields,
          }),
          vendorId: relevantMaterial.vendorId,
          vendor: vendors[relevantMaterial.vendorId]?.name ?? '',
        };
        delete newMaterial.quantity;
        return decorateMaterial(newMaterial, discount, quantityRemainingMode);
      })
      .filter(Boolean);

    const newSelected = decoratedSelected.concat(newMaterials);
    updateSelected(newSelected);
    setShowAdd(false);
  }, [
    decoratedSelected,
    materials,
    projectMaterialsMap,
    discount,
    quantityRemainingMode,
    updateSelected,
    costcodeMap,
    categoriesIdMap,
    allowedCostCodeCategoryIds,
    labourMappedCostcodeFields,
    vendors,
  ]);

  const onDelete = useCallback((rowId) => {
    const newSelected = decoratedSelected.filter((material) => material.id !== rowId);
    updateSelected(newSelected);
  }, [decoratedSelected, updateSelected]);

  const calculateNewAssemblyValues = (relevantMaterial, newQuantity) => {
    if (!relevantMaterial.isAssembly) return relevantMaterial;

    const newMaterial = { ...relevantMaterial };

    const {
      assemblyMaterials = [],
    } = newMaterial;
    const newAssemblyMaterials = assemblyMaterials.map((assemblyMaterial) => (
      decorateMaterial({
        ...assemblyMaterial,
        quantity: newQuantity * (assemblyMaterial.originalQuantity || 0),
      }, discount, quantityRemainingMode)
    ));

    newMaterial.assemblyMaterials = newAssemblyMaterials;
    return newMaterial;
  };

  const onQuantityChange = useCallback(({ id: rowId, value }) => {
    const newSelected = decoratedSelected.map((material) => {
      if (material.id !== rowId) return material;
      return decorateMaterial({
        ...calculateNewAssemblyValues(material, value),
        quantity: value,
        updatedDate: updateStackedDates(material.updatedDate),
      }, discount, quantityRemainingMode);
    });
    updateSelected(newSelected);
  }, [discount, quantityRemainingMode, decoratedSelected, updateSelected]);

  const getMaterialLocations = useCallback((material) => {
    let { locations } = material;
    if (!locations) {
      /*
        Locations is not stored with the submitted record
        so record.locations is empty on edit
        Pull from material list
        Project-sourced rows intentionally have no inventory — do not hydrate from global
      */
      if (material.globalMaterialId || projectMaterialsMap[material.materialId]) {
        locations = [];
      } else {
        locations = materials[material.materialId]?.locations ?? [];
      }
    }
    const opts = locations.map((loc) => {
      const { id: locId, equipmentId, globalMaterialLocationsId } = loc;
      const {
        [equipmentId]: { name: equipmentName } = {},
      } = equipmentIdMap;
      const {
        [globalMaterialLocationsId]: { locationText: globalMaterialLocationsText } = {},
      } = globalMaterialLocationsMap;
      return {
        label: equipmentName || globalMaterialLocationsText,
        value: locId,
        globalMaterialLocationsId,
        equipmentId,
        isLeaf: true,
      };
    });
    return opts;
  }, [equipmentIdMap, globalMaterialLocationsMap, projectMaterialsMap, materials]);

  const findMaterialsWithSameLocation = useCallback((selectedMaterials, relevantLocationId) => {
    const materialsWithSameLocation = [];

    selectedMaterials.forEach((material) => {
      const relevantLocations = getMaterialLocations(material);
      if (
        relevantLocations.some((loc) => (
          loc.globalMaterialLocationsId === relevantLocationId
          || loc.equipmentId === relevantLocationId
        ))
      ) {
        materialsWithSameLocation.push({ id: material.id, name: material.name });
      }

      if (material.assemblyMaterials?.length) {
        materialsWithSameLocation.push(
          ...findMaterialsWithSameLocation(material.assemblyMaterials, relevantLocationId),
        );
      }
    });
    return materialsWithSameLocation;
  }, [getMaterialLocations]);

  const updateMaterial = (updateMaterialIdSet, material, newData) => {
    if (!updateMaterialIdSet.has(material.id)) return material;
    const formattedData = { ...newData };
    const relevantLocationKey = getRelevantKey(newData);

    if (
      relevantLocationKey
    ) {
      let relevantLocation = getMaterialLocations(material).find((loc) => (
        loc[relevantLocationKey.materialKey] === newData[relevantLocationKey.newDataKey]
      ));

      // If no relevant location is found, check if it's a global location
      if (!relevantLocation && newData.toGlobalMaterialLocationsId) {
        const globalLocation = globalMaterialLocationsMap[newData.toGlobalMaterialLocationsId];
        if (globalLocation) {
          relevantLocation = {
            value: globalLocation.id,
            label: globalLocation.locationText,
            globalMaterialLocationsId: globalLocation.id,
          };
        }
      }

      if (relevantLocation) {
        formattedData[relevantLocationKey.isToLocation ? 'toLocation' : 'locationId'] = relevantLocation.value;
        formattedData[relevantLocationKey.isToLocation ? 'toLocationText' : 'locationText'] = relevantLocation.label;
      }
    }

    if ('price' in newData) {
      formattedData.userPrice = newData.price;
    }

    if ('labourPrice' in newData) {
      formattedData.userLabourPrice = newData.labourPrice;
    }

    if (!('updatedDate' in newData)) {
      formattedData.updatedDate = updateStackedDates(material.updatedDate);
    }

    if ('debitOrCredit' in newData && material.assemblyMaterials?.length) {
      formattedData.assemblyMaterials = material.assemblyMaterials.map((assemblyMaterial) => ({
        ...assemblyMaterial,
        debitOrCredit: newData.debitOrCredit || 'Debit',
      }));
    }

    const hasChanges = Object.keys(formattedData).some((key) => (
      formattedData[key] !== material[key]
    ));

    if (hasChanges) formattedData.isPreloaded = false;

    return decorateMaterial({
      ...material,
      ...formattedData,
    }, discount, quantityRemainingMode);
  };

  const updateMaterials = (updateMaterialIdSet, newData) => {
    const newSelected = decoratedSelected.map((material) => {
      const updatedMaterial = updateMaterial(updateMaterialIdSet, material, newData);
      updatedMaterial.assemblyMaterials = updatedMaterial.assemblyMaterials?.map(
        (assemblyMaterial) => updateMaterial(updateMaterialIdSet, assemblyMaterial, newData),
      );
      return updatedMaterial;
    });
    updateSelected(newSelected);
  };

  const onValueChanged = useCallback((rowId, newData = {}) => {
    const relevantId = (
      newData.globalMaterialLocationsId
      || newData.equipmentId
      || newData.toGlobalMaterialLocationsId
      || newData.toEquipmentId
    );
    if (
      relevantId
      && !newData.isDefaultUpdate
    ) {
      const nonUpdatedMaterials = decoratedSelected.filter((material) => material.id !== rowId);
      // toGlobalMaterialLocationsId is a special case for some reason and is available
      // to all materials, even if not assigned so we treat all materials as eligible
      const materialsWithSameLocation = newData.toGlobalMaterialLocationsId
        ? nonUpdatedMaterials
        : findMaterialsWithSameLocation(
          nonUpdatedMaterials,
          relevantId,
        );

      if (materialsWithSameLocation.length > 0) {
        CustomConfirmModal({
          title: 'Update other materials with the same location?',
          content: (
            <div>
              The selected material is located in the same location as other materials.
              Would you like to update the location of these materials as well?
              <br />
              <br />
              <b>Materials:</b> {materialsWithSameLocation.map((material) => material.name).join(', ')}
            </div>
          ),
          okText: 'Yes',
          cancelText: 'No',
          onOk: () => {
            updateMaterials(
              new Set([rowId, ...materialsWithSameLocation.map((material) => material.id)]),
              newData,
            );
          },
          onCancel: () => {
            updateMaterials(
              new Set([rowId]),
              newData,
            );
          },
        });
        return;
      }
    }

    const { isDefaultUpdate, ...nextData } = newData;
    updateMaterials(new Set([rowId]), nextData);
  }, [discount, decoratedSelected, updateSelected, materials, findMaterialsWithSameLocation]);

  const onCostcodeUpdated = useCallback((rowId, selectedVal) => {
    onValueChanged(rowId, getFormTableCostcodeUpdateFields({
      selectedVal,
      phaseMap,
      costcodeMap,
      categoriesIdMap,
      categoryFilter: MATERIAL_CATEGORY_FILTER,
      allowedCategoryIds: allowedCostCodeCategoryIds,
      formatCostcode: formatFormTableCostcodeName,
    }));
  }, [onValueChanged, phaseMap, costcodeMap, categoriesIdMap, allowedCostCodeCategoryIds]);

  const dataSource = useMemo(() => (
    isDisplay && !responding ? values : decoratedSelected
  ), [isDisplay, responding, values, decoratedSelected]);

  const hasPendingTableData = useMemo(
    () => dataSource.some((material) => material.pendingTableValue),
    [dataSource],
  );

  const getTableColumns = useCallback((isAssemblyMaterial = false) => (
    materialColumns({
      onDelete,
      onQuantityChange,
      onValueChanged,
      columns,
      columnKeyMap,
      isDisplay,
      discount,
      equipmentIdMap,
      globalMaterialLocationsMap,
      globalMaterialLocations,
      requiredColumns,
      materials,
      preventEdits,
      isAssemblyMaterial,
      columnPermissions,
      userPositionId,
      hasPendingTableData,
      costcodeMap,
      costcodes: tableCostcodes,
      filterCostcode,
      allowedCategoryIds: allowedCostCodeCategoryIds,
      onCostcodeUpdated,
      getMaterialLocations,
      onOpenFile,
      setCurrFileFieldId,
      setCurrAttachmentRowId,
      setOpenFileIndex,
      setCurrFieldId,
      previewProps,
      id,
      responding,
      vendors,
      divisions,
      projectId: linkedProjectIdForCostCodes,
      linkProjectCostCodes,
      responses,
    })
  ), [
    columns,
    columnKeyMap,
    isDisplay,
    onDelete,
    onQuantityChange,
    onValueChanged,
    discount,
    equipmentIdMap,
    globalMaterialLocationsMap,
    globalMaterialLocations,
    requiredColumns,
    materials,
    preventEdits,
    columnPermissions,
    userPositionId,
    hasPendingTableData,
    costcodeMap,
    tableCostcodes,
    filterCostcode,
    allowedCostCodeCategoryIds,
    onCostcodeUpdated,
    getMaterialLocations,
    onOpenFile,
    setCurrFileFieldId,
    setCurrAttachmentRowId,
    setOpenFileIndex,
    setCurrFieldId,
    previewProps,
    id,
    responding,
    vendors,
    divisions,
    linkedProjectIdForCostCodes,
    linkProjectCostCodes,
    responses,
  ]);

  const {
    maxExistingAllowed,
    shouldAddButtonBeEnabled = true,
  } = useCheckTableMaxRows({
    configProps,
    currentRowsLength: dataSource?.length,
  });

  const rowExpandable = useCallback((record) => !!record.isAssembly, []);
  const isExpandable = useMemo(
    () => dataSource.some((material) => material.isAssembly),
    [dataSource],
  );
  const onExpand = useCallback((event, { id: expandId }) => {
    if (!event) {
      setExpandedRowKeys(expandedRowKeys.filter((key) => key !== expandId));
      return;
    }

    setExpandedRowKeys([...expandedRowKeys, expandId]);
  }, [expandedRowKeys]);

  const expandedRowRender = useCallback((record) => (
    <Table
      columns={getTableColumns(true)}
      dataSource={record?.assemblyMaterials || []}
      pagination={false}
      size="small"
      bordered
      key="id"
      rowClassName={(rowRecord) => (rowRecord.pendingTableValue ? 'pending-table-value' : '')}
    />
  ), [materials, getTableColumns]);

  const expandable = useMemo(() => (
    isExpandable ? {
      expandedRowKeys,
      onExpand,
      rowExpandable,
      expandedRowRender,
    } : null
  ), [isExpandable, expandedRowKeys, onExpand, rowExpandable, expandedRowRender]);

  const handleReloadPreload = useCallback(
    createHandleReloadPreload({
      responses,
      id,
      onReload: triggerPreload,
    }),
    [triggerPreload, responses, id],
  );

  const { preloadExistingEntries = false } = configProps ?? {};
  const showReloadButton = preloadExistingEntries && responding && !isDisplay;

  return (
    <>
      <Row style={{ marginTop: showCondensedView ? 0 : 15 }}>
        <Row justify="space-between" style={{ width: '100%', marginBottom: 10 }}>
          <Col>
            {title}
          </Col>
          {!isDisplay && !loading && (
            <Space gutter={10}>
              {showReloadButton && (
                <OnTraccrButton
                  title="Reload Preloaded Data"
                  onClick={handleReloadPreload}
                  icon={<ReloadOutlined />}
                />
              )}
              {discount ? (
                <HoverHelp
                  content={discount > 0
                    ? `Applying ${discount * 100}% discount to material prices that have not been changed`
                    : `Applying ${Math.abs(discount * 100)}% markup to material prices that have not been changed`}
                  iconProps={{ style: { color: 'red', marginRight: 10 } }}
                />
              ) : null}
              <Dropdown
                placement="bottomRight"
                trigger={['click']}
                overlay={(
                  <Menu
                    onClick={({ key }) => {
                      switch (key) {
                        case 'addMaterial':
                          showAddDrawer();
                          break;
                        case 'addNew':
                          onAddNew();
                          break;
                        default:
                          break;
                      }
                    }}
                  >
                    {!hideAddNewButton && (
                      <Menu.Item key="addNew" disabled={!shouldAddButtonBeEnabled}>
                        Add Row
                      </Menu.Item>
                    )}
                    <Menu.Item key="addMaterial" disabled={!shouldAddButtonBeEnabled}>
                      Add Material
                    </Menu.Item>
                  </Menu>
                )}
              >
                <OnTraccrButton
                  title="Add"
                  icon={<DownOutlined />}
                  disabled={!shouldAddButtonBeEnabled}
                />
              </Dropdown>
            </Space>
          )}
        </Row>
        { !showCondensedView || dataSource?.length ? (
          <Table
            className="form-table-preview"
            style={{ width: '100%', overflow: 'auto' }}
            columns={getTableColumns()}
            size="small"
            pagination={false}
            dataSource={dataSource}
            expandable={expandable}
            rowKey="id"
            loading={loading}
            rowClassName={(rowRecord) => (rowRecord.pendingTableValue ? 'pending-table-value' : '')}
          />
        ) : (
          <DisplayText title="No Materials Selected" style={{ marginBottom: 0 }} />
        )}
      </Row>
      <MaterialTableAddDrawer
        visible={showAdd}
        onClose={closeAddDrawer}
        onSelect={onSelect}
        onSubmit={onSelect}
        materials={materials}
        projectMaterials={projectMaterials}
        existingMaterials={selected}
        maxExistingAllowed={maxExistingAllowed}
      />
    </>
  );
}

MaterialTablePreview.propTypes = {
  title: PropTypes.node,
  materials: PropTypes.objectOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    price: PropTypes.number,
    quantity: PropTypes.number,
    equipmentId: PropTypes.string,
    locationId: PropTypes.string,
  })),
  projectMaterials: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    globalMaterialId: PropTypes.string,
  })),
  columns: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    dataIndex: PropTypes.string,
    key: PropTypes.string,
    editable: PropTypes.bool,
    required: PropTypes.bool,
    type: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })),
  })),
  previewProps: PropTypes.shape({
    values: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOf([
        PropTypes.string,
        PropTypes.number,
      ]),
      name: PropTypes.string,
      price: PropTypes.number,
      quantity: PropTypes.number,
      equipmentId: PropTypes.string,
      locationId: PropTypes.string,
    })),
    selected: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.number,
      quantity: PropTypes.number,
      equipmentId: PropTypes.string,
      locationId: PropTypes.string,
    })),
  }),
  setPreviewProps: PropTypes.func,
  isDisplay: PropTypes.bool,
  id: PropTypes.string,
  setResponses: PropTypes.func,
  responses: PropTypes.objectOf(PropTypes.shape({
    values: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.number,
      quantity: PropTypes.number,
      equipmentId: PropTypes.string,
      locationId: PropTypes.string,
    })),
    selected: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.number,
      quantity: PropTypes.number,
      equipmentId: PropTypes.string,
      locationId: PropTypes.string,
    })),
    columns: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string,
      dataIndex: PropTypes.string,
      key: PropTypes.string,
      editable: PropTypes.bool,
      required: PropTypes.bool,
      type: PropTypes.string,
      options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
      })),
    })),
  })),
  responding: PropTypes.bool,
  projectId: PropTypes.string,
  customerId: PropTypes.string,
  configProps: PropTypes.shape({
    hideAddNewButton: PropTypes.bool,
    requiredColumns: PropTypes.bool,
  }),
  showCondensedView: PropTypes.bool,
  costcodes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  divisions: PropTypes.arrayOf(PropTypes.string),
  onOpenFile: PropTypes.func,
  setCurrFileFieldId: PropTypes.func,
  setCurrAttachmentRowId: PropTypes.func,
  setOpenFileIndex: PropTypes.func,
  setCurrFieldId: PropTypes.func,
  timecardLabourCostcodeKey: PropTypes.string,
  timecardLabourPhaseId: PropTypes.string,
};

MaterialTablePreview.defaultProps = {
  title: null,
  materials: {},
  projectMaterials: undefined,
  columns: [],
  previewProps: {},
  setPreviewProps: () => {},
  isDisplay: false,
  id: null,
  setResponses: null,
  responses: {},
  responding: false,
  projectId: null,
  customerId: null,
  configProps: {},
  showCondensedView: false,
  costcodes: [],
  divisions: [],
  onOpenFile: () => {},
  setCurrFileFieldId: () => {},
  setCurrAttachmentRowId: () => {},
  setOpenFileIndex: () => {},
  setCurrFieldId: () => {},
  timecardLabourCostcodeKey: null,
  timecardLabourPhaseId: null,
};
